'use client'

import React, { useState } from 'react'

import { Box, Square } from '@chakra-ui/react'

import { BadgeCounter } from '../BadgeCounter'
import { Tooltip } from '../Tooltip/Tooltip'
import {
  ColorScheme,
  SIDE_NAV_WIDTH_CLOSED,
  sideNavIconStyles,
  sideNavItemWrapperStyles,
  sideNavLabelStyles,
} from './SideNavItem.styles'

export type SideNavItemProps = {
  icon: React.ElementType
  label: string
  isActive: boolean
  isExpanded: boolean
  badge?: React.ReactNode
  iconSize?: string
  colorScheme?: ColorScheme
  'data-testid'?: string
  onClick?: () => void
}

export const SideNavItem = ({
  icon,
  label,
  isActive,
  isExpanded,
  badge,
  iconSize = '20px',
  colorScheme = 'dark',
  'data-testid': dataTestId,
  onClick,
}: SideNavItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const iconElement = (
    <Box
      alignItems='center'
      // Constrain the rendered SVG to iconSize regardless of the icon's own
      // width/height attributes. SVG preserveAspectRatio keeps non-square
      // icons proportional (letterboxed) rather than stretched.
      css={{ '& svg': { width: iconSize, height: iconSize } }}
      display='flex'
      flexShrink={0}
      h={iconSize}
      justifyContent='center'
      w={iconSize}
      {...sideNavIconStyles(isActive, colorScheme)}
    >
      {React.createElement(icon)}
    </Box>
  )

  return (
    <Box
      {...sideNavItemWrapperStyles(isActive, isExpanded, colorScheme)}
      data-testid={dataTestId}
      onClick={onClick}
    >
      {isExpanded ? (
        // Expanded: the wrapper supplies the 20px left padding + 12px icon gap
        // (ENG-33971), so the icon renders directly, followed by the label and
        // its badge separated by a 6px gap.
        <>
          {iconElement}
          <Box alignItems='center' display='flex' gap='6px' overflow='hidden'>
            <Box {...sideNavLabelStyles(isActive, colorScheme)}>{label}</Box>
            {badge}
          </Box>
        </>
      ) : (
        // Collapsed: the icon stays centered in the 60px-wide rail (48px tall to
        // match the expanded row height), with the badge reduced to a
        // notification dot and the label shown on hover.
        <>
          <Square
            h='48px'
            onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
            onMouseLeave={() => setAnchorEl(null)}
            position='relative'
            w={SIDE_NAV_WIDTH_CLOSED}
          >
            {badge && (
              <Box lineHeight={0} position='absolute' right='10px' top='12px'>
                <BadgeCounter
                  colorScheme={colorScheme}
                  variant='notification-dot'
                />
              </Box>
            )}
            {iconElement}
          </Square>
          <Tooltip
            anchorEl={anchorEl}
            colorScheme={colorScheme}
            label={label}
            placement='right'
          />
        </>
      )}
    </Box>
  )
}
